import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { gaTrack } from "@/lib/analytics";
import { ASK_PATH, CATEGORIES, MARKET_CSS } from "@/lib/marketBrowse";
import { isAuthenticated } from "../utils/fetchWithAuth";
import { useBranding } from "../contexts/BrandingContext";
import AccountMenu from "./AccountMenu";
import PhIcon from "./PhIcon";

/* The marketplace's green header: logo, search, account, and the category
   strip. Every browse surface wears it -- the homepage and /browse, a post, the
   category pages -- so moving between them feels like one place.

   On a browse page (pages/MarketHome) the search and the strip drive that
   page's filters, through `search` and `onCategory`. Anywhere else they are
   ways into browse: searching or picking a category goes to /browse with it.
   Styles are MARKET_CSS in lib/marketBrowse.js; the page includes them.

   On a partner portal the partner's own name, logo and colour replace Sift's
   (BrandingContext). */

export function MarketSearch({ className, show, setShow, text, setText, where, setWhere, onSubmit }) {
  return (
    <form role="search" className={`mh-search ${className}`} onSubmit={onSubmit}>
      <label className="mh-show">
        <span className="mh-sr">Show</span>
        <select value={show} onChange={(e) => setShow(e.target.value)}>
          <option value="all">All</option>
          <option value="person">People</option>
          <option value="service">Services</option>
          <option value="job">Jobs</option>
        </select>
      </label>
      <label className="mh-field mh-field-what">
        <PhIcon name="magnifying-glass" size={18} />
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Role, service or skill" aria-label="What" />
      </label>
      <label className="mh-field mh-field-where">
        <PhIcon name="map-pin" size={18} />
        <input value={where} onChange={(e) => setWhere(e.target.value)} placeholder="City or remote" aria-label="Where" />
      </label>
      <button type="submit" className="mh-go" aria-label="Search"><PhIcon name="magnifying-glass" size={22} /></button>
    </form>
  );
}

export default function MarketHeader({ search, category = "all", onCategory, onHow, surface = "page" }) {
  const branding = useBranding();
  const brand = branding.branded
    ? { name: branding.brand_name, logoUrl: branding.brand_logo_url, color: branding.accent_color }
    : null;
  const navigate = useNavigate();
  const loggedIn = isAuthenticated();
  const [menuOpen, setMenuOpen] = useState(false); // phone menu
  const [catOpen, setCatOpen] = useState(false); // phone category dropdown

  // Off a browse page the search keeps its own state and hands off to /browse.
  const [own, setOwn] = useState({ show: "all", text: "", where: "" });
  const toBrowse = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (own.text.trim()) p.set("q", own.text.trim());
    if (own.where.trim()) p.set("location", own.where.trim());
    if (own.show !== "all") p.set("show", own.show);
    navigate(`/browse${p.toString() ? `?${p}` : ""}`);
  };
  const searchProps = search || {
    show: own.show, setShow: (v) => setOwn((o) => ({ ...o, show: v })),
    text: own.text, setText: (v) => setOwn((o) => ({ ...o, text: v })),
    where: own.where, setWhere: (v) => setOwn((o) => ({ ...o, where: v })),
    onSubmit: toBrowse,
  };

  useEffect(() => {
    if (!menuOpen && !catOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") { setMenuOpen(false); setCatOpen(false); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen, catOpen]);

  const pick = (c) => {
    setCatOpen(false);
    if (onCategory) return onCategory(c);
    return navigate(c.key === "all" ? "/browse" : `/browse?category=${c.key}`);
  };
  // Off a browse page no category is picked, so the phone button just says what it opens.
  const currentCat = CATEGORIES.find((c) => c.key === category) || { label: "Categories", icon: "squares-four" };
  const getStarted = () => gaTrack("cta_get_started", { location: `${surface}_header`, account_type: "unknown" });
  const headStyle = brand?.color ? { background: brand.color } : undefined;

  return (
    <header className="mh-head" style={headStyle}>
      <div className="mh-wrap mh-bar">
        <Link to="/" className="mh-logo">
          {brand?.logoUrl ? <img src={brand.logoUrl} alt={brand.name || ""} style={{ height: 32, maxWidth: 180, objectFit: "contain" }} /> : (brand?.name || "Sift Careers")}
        </Link>
        <MarketSearch className="mh-search-head" {...searchProps} />
        <nav aria-label="Account" className="mh-acct">
          {loggedIn ? (
            <AccountMenu size={36} />
          ) : (
            <>
              <Link to="/auth/register" className="mh-acct-link mh-hide-sm">Offer your work</Link>
              <Link to="/auth/login" className="mh-acct-link mh-hide-phone">Sign in</Link>
              <Link to={ASK_PATH} className="mh-btn mh-btn-cream" onClick={getStarted}>Get started</Link>
              <button type="button" className="mh-burger" aria-label="Menu" aria-expanded={menuOpen} aria-controls="mh-menu" onClick={() => setMenuOpen((o) => !o)}>
                <PhIcon name={menuOpen ? "x" : "list"} size={24} />
              </button>
            </>
          )}
        </nav>
      </div>
      {menuOpen && (
        <nav id="mh-menu" aria-label="Menu" className="mh-menu">
          {onHow && <button type="button" onClick={() => { setMenuOpen(false); onHow("menu"); }}>How Sift works</button>}
          <Link to="/browse" onClick={() => setMenuOpen(false)}>Browse</Link>
          <Link to="/auth/register" onClick={() => setMenuOpen(false)}>Offer your work</Link>
          <Link to="/auth/login" onClick={() => setMenuOpen(false)}>Sign in</Link>
        </nav>
      )}
      <div className="mh-catband">
        {/* Phones: one button naming the current category, opening the full
            list. A scrolling strip hid most categories past the edge. */}
        <div className="mh-wrap mh-catpick">
          <button type="button" className="mh-catpick-btn" aria-expanded={catOpen} aria-controls="mh-catlist" onClick={() => setCatOpen((o) => !o)}>
            <PhIcon name={currentCat.icon} size={22} />
            <span className="mh-grow">{currentCat.label}</span>
            <PhIcon name="caret-down" size={18} style={{ transform: catOpen ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
          </button>
          {catOpen && (
            <ul id="mh-catlist" className="mh-catlist">
              {CATEGORIES.map((c) => (
                <li key={c.key}>
                  <button type="button" aria-pressed={category === c.key} className={category === c.key ? "on" : ""} onClick={() => pick(c)}>
                    <PhIcon name={c.icon} size={22} />
                    <span className="mh-grow">{c.label}</span>
                    {category === c.key && <PhIcon name="check" size={18} />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <nav aria-label="Categories" className="mh-wrap mh-cats">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              type="button"
              className={`mh-cat${category === c.key ? " on" : ""}`}
              aria-pressed={category === c.key}
              onClick={() => pick(c)}
            >
              <PhIcon name={c.icon} size={24} />
              {c.label}
            </button>
          ))}
        </nav>
      </div>
    </header>
  );
}

/* The header on a page that is not itself a browse page (the category and
   fractional landing pages): the marketplace styles, the header, then the
   page. Searching or picking a category from here goes to /browse. */
export function MarketShell({ children, surface = "category" }) {
  return (
    <>
      <div className="mh">
        <style>{MARKET_CSS}</style>
        <MarketHeader surface={surface} category="" />
      </div>
      {children}
    </>
  );
}
